import React, { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { notificationsApi } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';

export const NotificationBell = () => {
  const { t } = useLang();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await notificationsApi.getAll();
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unreadCount > 0) {
      try {
        await notificationsApi.markAllRead();
        setUnreadCount(0);
      } catch (error) {
        console.error('Error marking notifications read:', error);
      }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleToggle}
        data-testid="notification-bell"
        className="relative text-[#1C1917] hover:text-[#4A5D23] transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-[#C05746] text-white text-xs rounded-full"
          >
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-3 w-80 bg-white border border-[#E5E0D8] shadow-lg z-50 max-h-96 overflow-y-auto"
        >
          <div className="px-4 py-3 border-b border-[#E5E0D8] flex items-center justify-between">
            <span className="text-sm font-medium text-[#1C1917]">{t({ it: 'Notifiche', en: 'Notifications' })}</span>
            <CheckCheck className="w-4 h-4 text-[#4A5D23]" />
          </div>
          {notifications.length === 0 ? (
            <p className="px-4 py-8 text-sm text-[#57534E] text-center">
              {t({ it: 'Nessuna notifica', en: 'No notifications' })}
            </p>
          ) : (
            notifications.map((n, idx) => (
              <div
                key={n.notification_id}
                data-testid={`notification-item-${idx}`}
                className="px-4 py-3 border-b border-[#F2EFE9] last:border-0"
              >
                <p className="text-sm font-medium text-[#4A5D23] mb-1">{n.title}</p>
                <p className="text-xs text-[#57534E] leading-relaxed">{n.message}</p>
                <p className="text-[10px] text-[#A8A29E] mt-1">
                  {new Date(n.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
